import { Grid, LoadMore, StoriesListSection, Title } from './styles';
import Button from '@/components/UI/Button';
import { inject, observer } from 'mobx-react';
import { IStoriesStore } from '@/stores/Stories';
import { FC, useEffect, useRef, useState } from 'react';
import { StoryCard } from '@/components/Story';
import GalleryLoading from '@/scenes/Gallery/Loading';
import { gsap, Power4 } from 'gsap';
import { SplitText } from '@/utils/gsap/SplitText';
import uniq from 'lodash.uniq';

interface StoriesListProps {
  storiesStore: IStoriesStore;
}

const StoriesList: FC<any> = ({
  storiesStore: { visibleItems, setVisibleItems, total, loading, loadItems },
}: StoriesListProps) => {
  const [loaded, setLoaded] = useState(false);

  const animatedTextItems = useRef([]);
  const fadeItemsRef = useRef([]);

  useEffect(() => {
    setLoaded(true);

    return () => {
      setVisibleItems(visibleItems.slice(0, 6));
    };
  }, []);

  useEffect(() => {
    if (!loaded) return;

    uniq(fadeItemsRef.current.filter((i) => i)).forEach((element) => {
      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: 'top 92%',
        },
      });
      tl.fromTo(
        element,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.75,
          ease: Power4.easeInOut,
        },
      );
    });

    uniq(animatedTextItems.current.filter((i) => i)).forEach((element) => {
      const childSplit = new SplitText(element, {
        type: 'lines',
        linesClass: 'animatedLineChild',
      });
      const parentSplit = new SplitText(element, {
        type: 'lines',
        linesClass: 'animatedLineParent',
      });

      let tl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: 'top 92%',
        },
      });
      tl.fromTo(
        childSplit.lines,
        {
          transition: 'none',
          transformOrigin: '0% 0%',
          scaleY: 1,
          yPercent: 100,
        },
        {
          duration: 0.75,
          transformOrigin: '0% 0%',
          scaleY: 1,
          opacity: 1,
          y: 0,
          yPercent: 0,
          stagger: 0.075,
          ease: Power4.easeInOut,
          onComplete: () => {
            // @ts-ignore
            childSplit.revert();
          },
        },
      ).set(parentSplit.lines, { overflow: 'visible' }, 0.2);
    });

    gsap.utils.toArray('.animated-cards article').forEach((element) => {
      const animatedCardsTl = gsap.timeline({
        scrollTrigger: {
          // @ts-ignore
          trigger: element,
          start: 'top 92%',
          // markers: true,
        },
      });

      animatedCardsTl.fromTo(
        // @ts-ignore
        element,
        {
          opacity: 0,
          yPercent: 20,
        },
        {
          yPercent: 0,
          opacity: 1,
          delay: 0.2,
          duration: 0.75,
          ease: Power4.easeInOut,
        },
      );
    });
  }, [loaded]);

  return (
    <StoriesListSection>
      <div className="container">
        <Title ref={(r) => animatedTextItems.current.push(r)}>Stories</Title>
        <Grid
          ref={(r) => {
            fadeItemsRef.current.push(r);
          }}
          className="animated-cards"
        >
          {visibleItems.map((story) => (
            <StoryCard key={story.slug} compact story={story} />
          ))}
        </Grid>
        {visibleItems.length < total && (
          <LoadMore>
            {!loading && (
              <Button
                // @ts-ignore
                ref={(r) => fadeItemsRef.current.push(r)}
                type="secondary"
                colored
                onClick={() => loadItems()}
              >
                Show more
              </Button>
            )}
            {loading && <GalleryLoading small />}
          </LoadMore>
        )}
      </div>
    </StoriesListSection>
  );
};

export default inject('storiesStore')(observer(StoriesList));
